import { FC, RefObject, useEffect, useRef, useState } from 'react';

import RU from 'date-fns/locale/ru';
import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate, getDateFromString } from 'utils/date';

import {
    BottomSheet,
    BottomSheetFooter,
    Button,
    DatePicker,
    DateTimeInput,
    DateType,
    Drop,
    InputSize,
} from '@hh.ru/magritte-ui';
import { DropPlacement } from '@hh.ru/magritte-ui-drop/types';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

interface DateSelectProps {
    placeholder: string;
    selectedDate: Date | undefined;
    onDateSelect: (date: string) => void;
    maxDate?: Date;
    minDate?: Date;
    inputDisabled?: boolean;
    inputInvalid?: boolean;
    errorMessage?: string;
    placement?: DropPlacement | DropPlacement[];
    dropHost?: RefObject<HTMLElement>;
    elevatePlaceholder?: boolean;
    inputSize?: InputSize;
}

const TrlKeys = {
    apply: 'common.DateSelect.apply',
    close: 'common.DateSelect.close',
};

export const DateSelect: FC<DateSelectProps> = ({
    maxDate,
    minDate,
    onDateSelect,
    placeholder,
    selectedDate,
    inputDisabled,
    inputInvalid,
    errorMessage,
    placement,
    dropHost,
    elevatePlaceholder = false,
    inputSize = 'medium',
    ...restProps
}) => {
    const [isDatePickerVisible, setIsDatePickerVisibleOn, setIsDatePickerVisibleOff] = useOnOffState(false);
    const [displayDate, setDisplayDate] = useState<Date | DateType | undefined>(selectedDate || minDate);
    const [datePickerDate, setDatePickerDate] = useState<Date | undefined>(selectedDate);
    const initialInputValue = selectedDate ? formatDate(selectedDate, VIEW_DATE_FORMAT) : '';
    const [inputValue, setInputValue] = useState(initialInputValue);

    const activatorRef = useRef(null);
    const trls = useTrls();

    useEffect(() => {
        setDisplayDate(selectedDate);
        setInputValue(selectedDate ? formatDate(selectedDate, VIEW_DATE_FORMAT) : '');
    }, [selectedDate]);

    const changeDisplayDate = (date: DateType) => {
        setDisplayDate(date instanceof Date ? date : new Date(date));
    };

    const onDatePickerOpen = () => {
        setIsDatePickerVisibleOn();
        setDisplayDate(selectedDate || minDate);
        setDatePickerDate(selectedDate);
    };

    const onDatePickerSelect = () => {
        if (!datePickerDate) {
            return;
        }

        const newDate = datePickerDate;

        changeDisplayDate(newDate);
        setInputValue(formatDate(newDate, VIEW_DATE_FORMAT));
        setIsDatePickerVisibleOff();

        onDateSelect(formatDate(newDate));
    };

    const onInputBlur = () => {
        if (inputValue.length === 10) {
            let newDate = getDateFromString(inputValue, VIEW_DATE_FORMAT) as Date;

            if (minDate && newDate < minDate) {
                newDate = minDate;
            }

            if (maxDate && newDate > maxDate) {
                newDate = maxDate;
            }

            changeDisplayDate(newDate);
            setInputValue(formatDate(newDate, VIEW_DATE_FORMAT));
            onDateSelect(formatDate(newDate));
            return;
        }
        if (inputValue === '') {
            setInputValue('');
            onDateSelect('');
            setDisplayDate(undefined);
            return;
        }
        setInputValue(initialInputValue);
    };

    const datePicker = (
        <DatePicker
            alignHeader="left"
            enabledCalendars="dd.mm.yyyy"
            headerMonthFormat="mm.yyyy"
            selectedDate={datePickerDate}
            locale={RU}
            onDateSelect={(date) => setDatePickerDate(date as Date)}
            onDisplayDateChange={changeDisplayDate}
            displayDate={displayDate}
            minDate={minDate}
            maxDate={maxDate}
        />
    );

    const applyButton = (
        <Button
            data-qa="date-picker-apply"
            mode="primary"
            style={datePickerDate ? 'accent' : 'neutral'}
            onClick={() => {
                setIsDatePickerVisibleOff();
                datePickerDate && onDatePickerSelect();
            }}
            stretched
        >
            {trls(TrlKeys[datePickerDate ? 'apply' : 'close'])}
        </Button>
    );

    return (
        <>
            <DateTimeInput
                wrapperRef={activatorRef}
                elevatePlaceholder={elevatePlaceholder}
                size={inputSize}
                value={inputValue}
                placeholder={placeholder}
                dateMask="dd.mm.yyyy"
                disabled={inputDisabled}
                onBlur={onInputBlur}
                onChange={setInputValue}
                onButtonClick={onDatePickerOpen}
                invalid={inputInvalid}
                errorMessage={errorMessage}
                {...restProps}
            />
            <Drop
                visible={isDatePickerVisible}
                onClose={setIsDatePickerVisibleOff}
                activatorRef={activatorRef}
                placement={placement || 'top-left'}
                maxWidth={400}
                padding={0}
                paddingFooter={12}
                footer={applyButton}
                host={dropHost}
            >
                {datePicker}
            </Drop>
            <BottomSheet
                showDivider="always"
                onClose={setIsDatePickerVisibleOff}
                visible={isDatePickerVisible}
                withContentPaddings={false}
                footer={<BottomSheetFooter>{applyButton}</BottomSheetFooter>}
            >
                {datePicker}
            </BottomSheet>
        </>
    );
};
